import { auth } from '@/lib/firebase/config';
import { doc, setDoc, getDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { findUserByPhone, createCustomerStub, getUserData } from '@/lib/firebase/auth/user';
import { sendLoginOTP, sendOTP, verifyOTP } from '@/lib/services/otp-verification';
import type { OTPMethod } from '@/lib/services/authentica';

// OTP send/verify now run SERVER-SIDE via the sendOtpCode/verifyOtpCode
// callables (see functions/index.js and src/lib/services/otp-verification.ts,
// the already-hardened registration path). The Authentica API key never
// reaches the browser. This file keeps a lightweight `whatsappOTP/{id}`
// pointer doc — phone + flow only, no code/expiry/attempts — solely so
// verifyWhatsAppOTP can recover the phone from an opaque verificationId and
// so the existing `getCustomToken` callable (which still gates on that
// collection) keeps working unmodified.

interface WhatsAppOTPPointer {
  phone: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  otpMethod: OTPMethod;
  flow?: string | null;
  isVerified: boolean;
  verifiedAt?: Timestamp;
}

const OTP_EXPIRY_MINUTES = 5;

export async function sendWhatsAppOTP(
  phoneNumber: string,
  language: 'en' | 'ar' = 'en',
  flow?: string,
): Promise<{ success: boolean; verificationId?: string; error?: string; otpMethod?: OTPMethod }> {
  try {
    // Same gate as before: outside the request-treatment flow, an
    // unrecognised phone is rejected here, before any OTP is ever sent.
    const userExists = await findUserByPhone(phoneNumber);

    if (!userExists && flow !== 'request-treatment') {
      return {
        success: false,
        error: 'No account found with this phone number. Please check the number or create a new account.',
      };
    }

    // The server enforces purpose strictly: 'login' requires an existing
    // user, 'register' requires the phone NOT already be one. The old
    // client-side flow never made this distinction (it fired the same OTP
    // call either way), so it's reconstructed here from the user lookup
    // above — existing customers use 'login', and unrecognised numbers on
    // the request-treatment flow use 'register' so they can still receive
    // an OTP and be turned into a customer stub after verification below.
    const result = userExists
      ? await sendLoginOTP(phoneNumber, language)
      : await sendOTP(phoneNumber, language);

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to send OTP' };
    }

    const verificationId = `otp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = Timestamp.now();
    const expiresAt = new Timestamp(now.seconds + (OTP_EXPIRY_MINUTES * 60), now.nanoseconds);
    const otpMethod = result.otpMethod || 'whatsapp';

    const pointer: WhatsAppOTPPointer = {
      phone: phoneNumber,
      createdAt: now,
      expiresAt,
      otpMethod,
      flow: flow ?? null,
      isVerified: false,
    };

    await setDoc(doc(db, 'whatsappOTP', verificationId), pointer);

    return { success: true, verificationId, otpMethod };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function verifyWhatsAppOTP(
  verificationId: string,
  code: string
): Promise<{ success: boolean; userData?: any; error?: string; authSessionEstablished?: boolean }> {
  try {
    const pointerRef = doc(db, 'whatsappOTP', verificationId);
    const pointerDoc = await getDoc(pointerRef);

    if (!pointerDoc.exists()) {
      return { success: false, error: 'Verification code not found or expired' };
    }

    const pointer = pointerDoc.data() as WhatsAppOTPPointer;

    // Code validity, expiry and attempt-limits are all enforced server-side
    // now (verifyOtpCode) — this call is idempotent on an already-verified
    // record and returns mapped errors (otp-expired, too-many-attempts,
    // invalid-code, otp-not-found) via otp-verification.ts's conventions.
    const verifyResult = await verifyOTP(pointer.phone, code);
    if (!verifyResult.success) {
      return { success: false, error: verifyResult.error };
    }

    // Preferred path: the server proved the code and minted the token itself
    // (verifyOtpCode login grant). No client-written isVerified marker and no
    // getCustomToken call. If the sign-in fails we fall through to the legacy
    // path below so login still works.
    if (verifyResult.token) {
      try {
        const { signInWithCustomToken } = await import('firebase/auth');
        await signInWithCustomToken(auth, verifyResult.token);
        const grantedUser = verifyResult.userId
          ? await getUserData(verifyResult.userId)
          : await findUserByPhone(pointer.phone);
        await deleteDoc(pointerRef).catch(() => {});
        if (grantedUser) {
          return { success: true, userData: grantedUser, authSessionEstablished: true };
        }
        return { success: false, error: 'User data not found' };
      } catch (err) {
        console.warn('[auth-token] server-granted sign-in failed, using legacy path:', err);
      }
    }

    // Legacy path (no token: e.g. local-code OTP, request-treatment stub, or
    // an older functions deploy). Phase 2 removes it with getCustomToken.
    await setDoc(pointerRef, {
      ...pointer,
      isVerified: true,
      verifiedAt: Timestamp.now(),
    });

    let userData = await findUserByPhone(pointer.phone);

    if (!userData) {
      if (pointer.flow === 'request-treatment') {
        userData = await createCustomerStub(pointer.phone);
      } else {
        await deleteDoc(pointerRef);
        return { success: false, error: 'User data not found' };
      }
    }

    // Establish a real Firebase Auth session via custom token so Storage
    // uploads and Cloud-Function calls work for OTP-only users. Failure
    // here is non-fatal for the OTP itself but callers should surface it
    // because anything auth-gated will fail until the next successful
    // login. `authSessionEstablished` lets them react.
    let authSessionEstablished = false;
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { signInWithCustomToken } = await import('firebase/auth');
      const fns = getFunctions();
      const mint = httpsCallable<{ userId: string; otpVerificationId: string }, { token: string }>(
        fns,
        'getCustomToken',
      );
      const res = await mint({ userId: (userData as any).id, otpVerificationId: verificationId });
      const token = (res.data as any)?.token;
      if (token) {
        await signInWithCustomToken(auth, token);
        authSessionEstablished = true;
      }
    } catch (err) {
      console.warn('[auth-token] signInWithCustomToken failed:', err);
    }

    await deleteDoc(pointerRef);

    return { success: true, userData, authSessionEstablished };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}
